import axios from "axios";

// WhatsApp Business API client.
//
// Every call carries practiceNumber because the backend scopes each route to a
// practice the caller owns — there is no implicit "current practice" on the
// server, deliberately, since a user can belong to several.

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3002";

function authHeaders() {
  const token =
    sessionStorage.getItem("userToken") || sessionStorage.getItem("user");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const get = async (path, params) => {
  const { data } = await axios.get(`${URL}${path}`, {
    params,
    headers: authHeaders(),
  });
  return data;
};

const post = async (path, body) => {
  const { data } = await axios.post(`${URL}${path}`, body, {
    headers: authHeaders(),
  });
  return data;
};

/* ---- connection ---- */

export const getWhatsAppStatus = (practiceNumber) =>
  get("/whatsapp/status", { practiceNumber });

export const connectWhatsApp = ({ practiceNumber, code, wabaId, phoneNumberId }) =>
  post("/whatsapp/connect", { practiceNumber, code, wabaId, phoneNumberId });

export const disconnectWhatsApp = (practiceNumber) =>
  post("/whatsapp/disconnect", { practiceNumber });

/* ---- templates ---- */

export const listWhatsAppTemplates = (practiceNumber) =>
  get("/whatsapp/templates", { practiceNumber });

export const provisionWhatsAppTemplates = (practiceNumber, languages) =>
  post("/whatsapp/templates/provision", { practiceNumber, languages });

export const syncWhatsAppTemplates = (practiceNumber) =>
  post("/whatsapp/templates/sync", { practiceNumber });

export const deleteWhatsAppTemplate = async (practiceNumber, name) => {
  const { data } = await axios.delete(
    `${URL}/whatsapp/templates/${encodeURIComponent(name)}`,
    { params: { practiceNumber }, headers: authHeaders() },
  );
  return data;
};

/* ---- consent ---- */

// POPIA: no outbound message is sent without granted === true. Enforced
// server-side in the send path and the reminder cron, not here.
export const setWhatsAppConsent = async (patientId, granted, source) => {
  const { data } = await axios.patch(
    `${URL}/patients/${patientId}/whatsapp-consent`,
    { granted, source },
    { headers: authHeaders() },
  );
  return data;
};

// Absent consent reads as not granted, and the server normalises that, so the
// caller never has to know the difference between "no record" and "withdrawn".
export const getWhatsAppConsent = (patientId) =>
  get(`/patients/${patientId}/whatsapp-consent`);

/* ---- inbox ---- */

export const listConversations = (practiceNumber, { limit, cursor, filter } = {}) =>
  get("/whatsapp/conversations", { practiceNumber, limit, cursor, filter });

export const listMessages = (practiceNumber, conversationId, { limit, before } = {}) =>
  get(`/whatsapp/conversations/${conversationId}/messages`, {
    practiceNumber,
    limit,
    before,
  });

export const markConversationRead = (practiceNumber, conversationId) =>
  post(`/whatsapp/conversations/${conversationId}/read`, { practiceNumber });

export const linkConversation = (practiceNumber, conversationId, patientId) =>
  post(`/whatsapp/conversations/${conversationId}/link`, {
    practiceNumber,
    patientId,
  });

export const sendWhatsAppText = (practiceNumber, conversationId, body) =>
  post("/whatsapp/messages", { practiceNumber, conversationId, body });

export const sendWhatsAppTemplate = (
  practiceNumber,
  { conversationId, waId, templateName, languageCode, params },
) =>
  post("/whatsapp/messages/template", {
    practiceNumber,
    conversationId,
    waId,
    templateName,
    languageCode,
    params,
  });

export const whatsAppMediaUrl = (practiceNumber, waMessageId) =>
  `${URL}/whatsapp/media/${encodeURIComponent(waMessageId)}?practiceNumber=${encodeURIComponent(practiceNumber)}`;
